// Detail panels opened from map pins: airport movements (with the offshore
// helicopter breakdown), NorthLink ferry status, and rail departure boards.
//
// All three follow the same shape — fetch on open, refresh on an interval
// while open, and say plainly when there is nothing rather than rendering an
// empty box that reads as a fault.

import { bookingLabel, bookingUrl } from './booking.ts';

import { escapeHtml } from './escape.ts';
import { getJson } from './http.ts';

function head(title: string, sub: string): string {
  return `
    <div class="ap-head">
      <h2>${escapeHtml(title)} <span>${escapeHtml(sub)}</span></h2>
      <button id="ap-close" aria-label="Close">×</button>
    </div>`;
}

// ── Board tabs ──────────────────────────────────────────────────────────────
//
// Arrivals versus departures, shared by the rail, airport and bus station
// panels. Written once because the third copy is where they start to drift —
// and because the awkward part is not the markup but the swap: the data for
// every tab is already in hand, so switching must not re-fetch. A round trip
// to re-read what we are holding would make a tab feel slower than it is.

interface BoardTab<T extends string> {
  id: T;
  label: string;
  /** Shown beside the label, so an empty board is visible before it is opened. */
  count: number;
}

function tabStrip<T extends string>(tabs: readonly BoardTab<T>[], active: T): string {
  const one = (t: BoardTab<T>) => `
    <button type="button" class="brd-tab${t.id === active ? ' on' : ''}"
            data-tab="${escapeHtml(t.id)}" aria-pressed="${t.id === active}">
      ${escapeHtml(t.label)}<b>${t.count}</b>
    </button>`;
  return `<div class="brd-tabs">${tabs.map(one).join('')}</div>`;
}

/**
 * Wire a tab strip to a body element that re-renders in place.
 *
 * `onPick` is told which tab was chosen so the caller can remember it across
 * refreshes — a board that reset to departures every sixty seconds while
 * somebody was reading the arrivals would be worse than having no tabs.
 */
function wireTabs<T extends string>(
  el: HTMLElement,
  body: (tab: T) => string,
  onPick: (tab: T) => void,
): void {
  const target = el.querySelector<HTMLElement>('[data-role="brd-body"]');
  el.querySelectorAll<HTMLButtonElement>('.brd-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const picked = btn.dataset.tab as T;
      onPick(picked);
      el.querySelectorAll<HTMLButtonElement>('.brd-tab').forEach((b) => {
        const on = b.dataset.tab === picked;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', String(on));
      });
      if (target) target.innerHTML = body(picked);
    });
  });
}

// ── Airport, including the offshore helicopter view ─────────────────────────

interface Movement {
  id: string; callsign: string; type: string; operator: string;
  movement: 'arriving' | 'departing' | 'ground' | 'local';
  altitude: number | null; groundSpeed: number | null;
  distanceNm: number; rotary: boolean; lat: number; lon: number;
}

interface AirportPayload {
  airport: { icao: string; iata: string; name?: string };
  counts: Record<string, number>;
  movements: Movement[];
  note: string;
}

interface HeliPayload {
  outbound: number; inbound: number; onGround: number;
  operators: { operator: string; count: number }[];
  movements: Movement[];
  note: string;
}

const LABEL: Record<Movement['movement'], string> = {
  arriving: 'Arriving', departing: 'Departing', ground: 'On stand', local: 'In the area',
};

function movementRow(m: Movement): string {
  const arrow = m.movement === 'arriving' ? '↓'
    : m.movement === 'departing' ? '↑' : m.movement === 'ground' ? '•' : '·';
  const bits = [m.type, m.movement === 'ground' ? 'on the ground'
    : m.altitude !== null ? `${m.altitude.toLocaleString()} ft` : '',
  m.movement === 'arriving' || m.movement === 'departing' ? `${m.distanceNm} nm out` : '',
  m.groundSpeed !== null ? `${Math.round(m.groundSpeed)} kt` : ''].filter(Boolean);
  return `
    <li class="mv mv-${m.movement}" data-lat="${m.lat}" data-lon="${m.lon}">
      <span class="mv-arrow">${arrow}</span>
      <span class="mv-main">
        <span class="mv-callsign">${escapeHtml(m.callsign || m.id.toUpperCase())}${m.rotary ? ' <em>heli</em>' : ''}</span>
        <span class="mv-detail">${escapeHtml([m.operator, bits.join(' · ')].filter(Boolean).join(' — '))}</span>
      </span>
      <span class="mv-state">${LABEL[m.movement]}</span>
    </li>`;
}

type AirTab = 'dep' | 'arr' | 'all';

/**
 * Which movements belong on which board.
 *
 * `all` is a real tab rather than a fallback. Aircraft on stand and aircraft
 * simply in the area are most of what Aberdeen shows between offshore shift
 * changes, and they belong to neither board — putting them nowhere would mean
 * a panel that says "no departures" while eight helicopters sit on the apron.
 */
function movementsFor(movements: readonly Movement[], tab: AirTab): Movement[] {
  if (tab === 'all') return [...movements];
  const want = tab === 'dep' ? 'departing' : 'arriving';
  return movements.filter((m) => m.movement === want);
}

/**
 * Remembered across refreshes, module-level like the rail board's and for the
 * same reason: the panel re-renders every ten seconds and resetting the tab
 * each time would yank the arrivals board away mid-read.
 */
let airTab: AirTab = 'dep';

export async function renderAirport(el: HTMLElement): Promise<void> {
  const [air, heli] = await Promise.all([
    getJson<AirportPayload>('/airport'),
    getJson<HeliPayload>('/helicopters').catch(() => null),
  ]);

  const summary = [
    air.counts.arriving ? `${air.counts.arriving} arriving` : '',
    air.counts.departing ? `${air.counts.departing} departing` : '',
    air.counts.ground ? `${air.counts.ground} on stand` : '',
  ].filter(Boolean).join(' · ') || 'nothing moving right now';

  // The offshore block leads when there is rotary traffic: it is the thing
  // this map can show that no other map can.
  const heliBlock = heli && (heli.outbound + heli.inbound + heli.onGround > 0)
    ? `<div class="ap-block">
         <h3>Offshore helicopters</h3>
         <p class="ap-summary">${heli.outbound} outbound · ${heli.inbound} inbound · ${heli.onGround} on stand</p>
         ${heli.operators.length
      ? `<p class="ap-ops">${heli.operators.map((o) =>
        `${escapeHtml(o.operator)} <b>${o.count}</b>`).join(' · ')}</p>` : ''}
       </div>`
    : '';

  const body = (tab: AirTab): string => {
    const list = movementsFor(air.movements, tab);
    if (!list.length) {
      // Distinguishes an empty board from an empty sky. "No departures" with
      // six aircraft on the All tab is a different fact from nothing flying.
      if (air.movements.length) {
        return `<p class="ap-empty">No ${tab === 'dep' ? 'departures' : 'arrivals'} right now
          — ${air.movements.length} other aircraft in the area.</p>`;
      }
      return `<p class="ap-empty">No aircraft within 30 nm. Aberdeen is quiet at night and
        between offshore shift changes — this is usually the airport, not a fault.</p>`;
    }
    return `<ul class="ap-list">${list.map(movementRow).join('')}</ul>`;
  };

  const tabs = [
    { id: 'dep' as const, label: 'Departures', count: air.counts.departing ?? 0 },
    { id: 'arr' as const, label: 'Arrivals', count: air.counts.arriving ?? 0 },
    { id: 'all' as const, label: 'All', count: air.movements.length },
  ];

  el.innerHTML = `
    ${head(`${air.airport.name || 'Aberdeen'} Airport`, `${air.airport.iata} / ${air.airport.icao}`)}
    ${heliBlock}
    <p class="ap-summary">${escapeHtml(summary)}</p>
    ${tabStrip(tabs, airTab)}
    <div data-role="brd-body">${body(airTab)}</div>
    <p class="ap-note">${escapeHtml(air.note)}</p>`;

  wireTabs<AirTab>(el, body, (t) => { airTab = t; });
}

// ── NorthLink ferries ───────────────────────────────────────────────────────

/**
 * A ferry terminal with no tracked sailings — today, Rosyth.
 *
 * A factory rather than a renderer, because the pin knows the terminal's name
 * and nothing needs fetching: the terminal is a fact about the place, and the
 * panel says what is and is not being claimed. When a route actually runs from
 * here (Rosyth–Dunkirk is proposed but has no confirmed start date), the place
 * pack gains named vessels and the pin upgrades itself to the sailing board.
 */
export function renderFerryTerminal(name: string): (el: HTMLElement) => Promise<void> {
  return async (el) => {
    el.innerHTML = `
      ${head(name, 'ferry terminal')}
      <p class="ap-summary">No scheduled ferry service currently sails from here.</p>
      <p class="ap-note">Shown as a landmark. If a route starts running, its sailings can
        be tracked on this map the way the NorthLink boats are in Aberdeen.</p>`;
  };
}

/** A boat on the terminal's board. `origin` is set on arrivals only. */
interface Sailing extends StopDeparture {
  origin?: string;
}

type FerryTab = 'dep' | 'arr';

/** Remembered across refreshes, like the rail, airport and station boards. */
let ferryTab: FerryTab = 'dep';

/**
 * The terminal's boards, from the baked timetable — the bus station's own
 * presentation on the Ferries pin, sailings and arrivals tabbed apart.
 *
 * This panel showed AIS vessel status until 15 Aug 2026, and that data was
 * unreliable for the question being asked: the routes run beyond terrestrial
 * receiver coverage, so a mid-passage ferry read "not seen" for hours. The
 * booked times are what a person at the quay actually plans around — and the
 * arrivals tab is for the other half of the quay, meeting the overnight boat
 * that docks at 07:00. The ships layer still draws the vessels themselves
 * when AIS can see them.
 */
export async function renderFerries(el: HTMLElement): Promise<void> {
  const data = await getJson<{
    terminal: string; sailings: Sailing[]; arrivals?: Sailing[];
    hasArrivals?: boolean; note: string;
  }>('/ferries');

  const row = (s: Sailing, tab: FerryTab) => {
    // Destination on sailings, origin on arrivals — a terminating boat's
    // headsign is this terminal, which tells a reader standing at it nothing.
    const where = tab === 'dep'
      ? s.headsign || '—'
      : (s.origin ? `from ${s.origin}` : s.route ? `${s.route} sailing` : '—');
    return `
    <li class="dep">
      <span class="dep-time">${escapeHtml(s.route)}</span>
      <span class="dep-main">
        <span class="dep-dest">${escapeHtml(where)}</span>
        <span class="dep-detail">${escapeHtml([s.operator, s.time].filter(Boolean).join(' · '))}</span>
      </span>
      <span class="dep-status dep-sched">${escapeHtml(whenDue(s))}</span>
    </li>`;
  };

  const arrivals = data.arrivals ?? [];
  const showArrivals = Boolean(data.hasArrivals);
  // A bake without call flags cannot support the tab at all, so the remembered
  // choice must not strand the reader on a board that will never fill.
  if (!showArrivals) ferryTab = 'dep';

  const body = (tab: FerryTab): string => {
    if (tab === 'dep' && !data.sailings.length) {
      return '<p class="ap-empty">No more sailings scheduled today.</p>';
    }
    if (tab === 'arr' && !arrivals.length) {
      return '<p class="ap-empty">No more arrivals due today.</p>';
    }
    const list = tab === 'dep' ? data.sailings : arrivals;
    return `<ul class="ap-list dep-list">${list.map((s) => row(s, tab)).join('')}</ul>`;
  };

  const tabs = [
    { id: 'dep' as const, label: 'Sailings', count: data.sailings.length },
    { id: 'arr' as const, label: 'Arrivals', count: arrivals.length },
  ];

  el.innerHTML = `
    ${head('NorthLink ferries', 'Orkney & Shetland')}
    ${showArrivals ? tabStrip(tabs, ferryTab) : ''}
    <div data-role="brd-body">${body(ferryTab)}</div>
    <p class="ap-note">${escapeHtml(data.note)}</p>`;

  if (showArrivals) wireTabs<FerryTab>(el, body, (t) => { ferryTab = t; });
}

// ── Rail arrival and departure boards ───────────────────────────────────────

interface Service {
  std: string; etd: string; sta: string; eta: string;
  origin: string; destination: string;
  platform: string; operator: string;
  cancelled: boolean; delayed: boolean; reason: string;
  carriages: number | null;
  /** e.g. "Front 2 carriages only" at a platform shorter than the train. */
  doorWarning: string;
}
interface Board {
  crs: string; name: string; lat: number; lon: number;
  services: Service[]; message: string;
}

type Direction = 'dep' | 'arr';

/**
 * Which services belong on which board. A train that terminates here has no
 * departure time and a train that starts here has no arrival time, so the
 * absence of the time is the filter — no extra flag needed from the backend.
 */
function servicesFor(board: Board, dir: Direction): Service[] {
  return board.services.filter((s) => (dir === 'dep' ? s.std : s.sta));
}

/**
 * One service.
 *
 * `at` is passed only by the disruption list, where the rows come from several
 * stations and each needs to say which — and to carry that station's
 * coordinates, so the panel wiring can fly to it. A station board passes
 * nothing, because every row on it is at the station in the heading.
 */
function serviceRow(s: Service, dir: Direction, at?: Board, station?: string): string {
  const scheduled = dir === 'dep' ? s.std : s.sta;
  const estimate = dir === 'dep' ? s.etd : s.eta;
  // Where it is going, or where it came from — the useful half depends on
  // which board you are reading.
  const place = dir === 'dep' ? s.destination : s.origin;

  const status = s.cancelled ? 'cancelled' : s.delayed ? escapeHtml(estimate) : 'on time';
  const cls = s.cancelled ? 'dep-cancelled' : s.delayed ? 'dep-delayed' : 'dep-ontime';
  const detail = [
    s.operator,
    s.platform ? `Plat ${s.platform}` : '',
    s.carriages ? `${s.carriages} car` : '',
    s.reason,
  ].filter(Boolean).join(' · ');

  // The door warning gets its own line rather than joining the detail run-on.
  // At Portlethen this is the single most useful thing on the board — a
  // passenger in the rear carriage has to walk forward before the train stops,
  // and burying it mid-sentence after the operator name would waste it.
  const doors = s.doorWarning && !s.cancelled
    ? `<span class="dep-doors">⚠ ${escapeHtml(s.doorWarning)} — move to the front</span>`
    : '';

  // `mv` is what the panel wiring looks for to make a row fly the map to it.
  const classes = at ? `dep ${cls} mv` : `dep ${cls}`;
  const where = at ? `data-lat="${at.lat}" data-lon="${at.lon}"` : '';

  // Somewhere to buy a ticket for the train being looked at. Not on a
  // cancelled service: offering to sell a seat on a train that is not running
  // is the one thing a board must never do.
  //
  // `station` is absent on the disruption list, whose rows come from several
  // stations — and there the row already means "show me this", so a second
  // action competing with it would be worse than none.
  const book = station && !s.cancelled
    ? (() => {
      const journey = { here: station, other: place, time: scheduled, direction: dir } as const;
      return `<a class="dep-book" href="${escapeHtml(bookingUrl(journey))}"
                 target="_blank" rel="noopener noreferrer external"
                 title="${escapeHtml(bookingLabel(journey))}">Tickets</a>`;
    })()
    : '';

  return `
    <li class="${classes}" ${where}>
      <span class="dep-time">${escapeHtml(scheduled)}</span>
      <span class="dep-main">
        <span class="dep-dest">${escapeHtml(place)}</span>
        <span class="dep-detail">${escapeHtml(detail)}</span>
        ${at ? `<span class="dep-at">at ${escapeHtml(at.name)}</span>` : ''}
        ${doors}
      </span>
      <span class="dep-status">${status}${book}</span>
    </li>`;
}

function boardBody(board: Board, dir: Direction): string {
  const list = servicesFor(board, dir);
  if (!list.length) {
    return `<p class="ap-empty">No ${dir === 'dep' ? 'departures' : 'arrivals'} listed.</p>`;
  }
  return `<ul class="ap-list dep-list">${list.map((s) => serviceRow(s, dir, undefined, board.name)).join('')}</ul>`;
}

/**
 * Remembered across re-renders and across stations. The panel refreshes every
 * 60s, and resetting to departures each time would undo the user's choice
 * while they were still reading the arrivals board.
 */
let railDirection: Direction = 'dep';

export async function renderRail(el: HTMLElement, crs: string): Promise<void> {
  const data = await getJson<{ enabled: boolean; boards: Board[]; error: string }>('/rail');

  if (!data.enabled) {
    el.innerHTML = `${head('Rail', '')}
      <p class="ap-empty">Rail is off because <code>DARWIN_TOKEN</code> is not set.
      Register free at raildata.org.uk — and note that account approval alone is not enough,
      you must also subscribe to a Live Departure Boards product and use <em>that
      product's</em> consumer key, or the API returns 403.</p>`;
    return;
  }

  const board = data.boards.find((b) => b.crs === crs);
  if (!board) {
    el.innerHTML = `${head('Rail', crs)}
      <p class="ap-empty">No board yet${data.error ? ` — ${escapeHtml(data.error)}` : ''}.</p>`;
    return;
  }

  const body = (dir: Direction) => boardBody(board, dir);
  const tabs = [
    { id: 'dep' as const, label: 'Departures', count: servicesFor(board, 'dep').length },
    { id: 'arr' as const, label: 'Arrivals', count: servicesFor(board, 'arr').length },
  ];

  el.innerHTML = `
    ${head(board.name, board.crs)}
    ${board.message ? `<p class="ap-alert">${escapeHtml(board.message)}</p>` : ''}
    ${tabStrip(tabs, railDirection)}
    <div data-role="brd-body">${body(railDirection)}</div>
    <!-- Schedule 1 §8 of the data sharing agreement names Rail Delivery Group as
         the organisation that must be attributed. "Darwin" is what the service
         is called and what a reader recognises, but it is not the attribution. -->
    <p class="ap-note">Live boards © Rail Delivery Group, via National Rail Darwin.
    Ticket links go to <strong>traintickets.com</strong>, an independent Scottish
    retailer with no booking fee. We are not paid for them and earn nothing if
    you buy.</p>`;

  wireTabs<Direction>(el, body, (d) => { railDirection = d; });
}

/**
 * Every disrupted service, across all the stations we watch.
 *
 * The Live Status line said "3 trains delayed" and clicking it did nothing,
 * because the click handler looks for a map feature belonging to the provider
 * and rail has none — the trains are a vehicle layer and the boards are a
 * panel. Every other line on that card is a promise that clicking shows you
 * the thing; this one was a dead end.
 *
 * A disruption list rather than one station's board, because the delay is the
 * subject. Opening Aberdeen's full board would show forty on-time services and
 * leave the reader to find the three that are not, which is the work they
 * clicked to avoid. Each row carries its station's coordinates, so the panel
 * wiring already in place makes it fly there — which is the other half of what
 * a status line should do.
 *
 * No new endpoint: /rail already carries every board and every service, and
 * this is a filter over what the station panels are fetching anyway.
 */
export async function renderRailDisruption(el: HTMLElement): Promise<void> {
  const data = await getJson<{
    enabled: boolean; boards: Board[]; error: string;
    disruption?: (Service & { at: { crs: string; name: string; lat: number; lon: number } })[];
  }>('/rail');

  if (!data.enabled) {
    el.innerHTML = `${head('Rail', '')}
      <p class="ap-empty">Rail is off because <code>DARWIN_TOKEN</code> is not set.</p>`;
    return;
  }

  // One row per train, deduplicated by the backend across every board it
  // appears on. The client-side version keyed on the per-station scheduled
  // time, so the same late train at Haymarket 22:15 and South Gyle 22:20 was
  // two rows — which is exactly what the backend's merged journeys exist to
  // prevent.
  const rows = data.disruption ?? [];

  if (rows.length === 0) {
    el.innerHTML = `${head('Rail', 'disruption')}
      <p class="ap-empty">No delays or cancellations reported.</p>`;
    return;
  }

  const list = rows.map((service) =>
    serviceRow(service, service.std ? 'dep' : 'arr', { ...service.at, services: [], message: '' })).join('');

  el.innerHTML = `
    ${head('Rail', 'disruption')}
    <ul class="ap-list dep-list">${list}</ul>
    <p class="ap-note">Live boards © Rail Delivery Group, via National Rail Darwin.
    Ticket links go to <strong>traintickets.com</strong>, an independent Scottish
    retailer with no booking fee. We are not paid for them and earn nothing if
    you buy.</p>`;
}

// ── Bus stop departure boards ───────────────────────────────────────────────

interface StopDeparture {
  time: string;
  inMinutes: number;
  route: string;
  routeId: string;
  headsign: string;
  operator: string;
}

/**
 * When something is due, in the words a person waiting would use.
 *
 * "Due" rather than "0 min", and minutes only up to an hour — past that the
 * clock time is what you actually want, because nobody plans around "in 97
 * minutes".
 */
function whenDue(d: StopDeparture): string {
  if (d.inMinutes <= 0) return 'due';
  if (d.inMinutes < 60) return `${d.inMinutes} min`;
  return d.time;
}

/**
 * The `title` a departure row carries.
 *
 * A row whose route has no baked line is not clickable, which is correct and
 * was also completely silent: it looked exactly like a row nobody had clicked
 * yet, so "Stagecoach routes don't work" had no answer on screen (issue #39).
 *
 * Deliberately says the map is missing rather than blaming the operator. From
 * the reader's side the two causes — no shape published upstream, or a join
 * this repo got wrong — are the same absence, and the status page is where the
 * difference is now stated. A tooltip that says "not published" would be this
 * code excusing its own bug in the case where it has one.
 */
function rowTitle(d: { routeId?: string; route: string }): string {
  return d.routeId ? `Show the ${d.route} route` : `No route map for the ${d.route}`;
}

export async function renderStopBoard(el: HTMLElement, id: string, name: string): Promise<void> {
  const data = await getJson<{ departures: StopDeparture[]; note: string }>(
    `/stops/${encodeURIComponent(id)}`,
  );

  // Only rows whose route has baked geometry are clickable. 44 of Aberdeen's
  // routes have departures but no shape in the feed, and offering a link that
  // draws nothing is worse than not offering one.
  const rows = data.departures.map((d) => `
    <li class="dep${d.routeId ? ' dep-drawable' : ''}" title="${escapeHtml(rowTitle(d))}"${d.routeId ? ` data-route="${escapeHtml(d.routeId)}"` : ''}>
      <span class="dep-time">${escapeHtml(d.route)}</span>
      <span class="dep-main">
        <span class="dep-dest">${escapeHtml(d.headsign || '—')}</span>
        <span class="dep-detail">${escapeHtml(d.operator)} · ${escapeHtml(d.time)}</span>
      </span>
      <span class="dep-status dep-sched">${escapeHtml(whenDue(d))}${d.routeId ? ' <span class="dep-go">›</span>' : ''}</span>
    </li>`).join('');

  el.innerHTML = `
    ${head(name, 'bus stop')}
    ${data.departures.length
      ? `<ul class="ap-list dep-list">${rows}</ul>`
      : '<p class="ap-empty">Nothing more scheduled from this stop today.</p>'}
    <p class="ap-note">Scheduled times from the published timetable, not live
    tracking — a bus running late will still show its booked time.</p>`;
}

// ── Bus station ─────────────────────────────────────────────────────────────

interface StationDeparture extends StopDeparture {
  stance: string;
  stanceId: string;
  /** Where an arriving service started. Empty on departures. */
  origin?: string;
}

interface StationPayload {
  name: string;
  stances: number;
  nearbyStops: number;
  byName: boolean;
  departures: StationDeparture[];
  arrivals?: StationDeparture[];
  /** False when the baked timetable cannot tell an arrival from a departure. */
  hasArrivals?: boolean;
}

type StationTab = 'dep' | 'arr';

/** Remembered across refreshes, like the rail and airport boards. */
let stationTab: StationTab = 'dep';

/**
 * The bus station as one board.
 *
 * The stance is shown on every row rather than grouping by stance, because the
 * question at a bus station is "what leaves next, and where do I stand for it"
 * — and grouping answers the second half while burying the first.
 */
export async function renderBusStation(el: HTMLElement): Promise<void> {
  let data: StationPayload;
  try {
    data = await getJson<StationPayload>('/bus-station');
  } catch {
    // 503 while the timetable is unbaked is the common case and is not a fault
    // worth an error stack — it means the weekly bake has not run here yet.
    el.innerHTML = `${head('Bus station', '')}
      <p class="ap-empty">The timetable is not available right now.</p>`;
    return;
  }

  // The stance name repeats the station name in most feeds ("Aberdeen Bus
  // Station Stance 7"), so the station's own name is stripped from the row to
  // leave the part that tells someone where to stand. Built from the payload's
  // own name rather than a hardcoded city, so Edinburgh's stances shorten the
  // same way Aberdeen's do.
  const stationName = new RegExp(
    data.name.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+'),
    'i',
  );
  const shortStance = (s: string) => {
    const trimmed = s.replace(stationName, '').replace(/^[\s,–-]+/, '').trim();
    return trimmed || s;
  };

  const row = (d: StationDeparture, tab: StationTab) => {
    // Destination on the departures board, origin on arrivals — the useful
    // half depends on which you are reading. A terminating coach's headsign is
    // "Aberdeen", which tells a reader standing in Aberdeen nothing.
    const where = tab === 'dep'
      ? d.headsign || '—'
      : (d.origin ? `from ${d.origin}` : d.route ? `${d.route} service` : '—');
    return `
    <li class="dep${d.routeId ? ' dep-drawable' : ''}" title="${escapeHtml(rowTitle(d))}"${d.routeId ? ` data-route="${escapeHtml(d.routeId)}"` : ''}>
      <span class="dep-time">${escapeHtml(d.route)}</span>
      <span class="dep-main">
        <span class="dep-dest">${escapeHtml(where)}</span>
        <span class="dep-detail">${escapeHtml([d.operator, shortStance(d.stance), d.time].filter(Boolean).join(' · '))}</span>
      </span>
      <span class="dep-status dep-sched">${escapeHtml(whenDue(d))}${d.routeId ? ' <span class="dep-go">›</span>' : ''}</span>
    </li>`;
  };

  const arrivals = data.arrivals ?? [];
  const showArrivals = Boolean(data.hasArrivals);
  // A bake without call flags cannot support the tab at all, so the remembered
  // choice must not strand the reader on a board that will never fill.
  if (!showArrivals) stationTab = 'dep';

  const body = (tab: StationTab): string => {
    const list = tab === 'dep' ? data.departures : arrivals;
    if (!list.length) {
      return `<p class="ap-empty">Nothing more ${tab === 'dep' ? 'scheduled from' : 'due into'}
        the bus station today.</p>`;
    }
    return `<ul class="ap-list dep-list">${list.map((d) => row(d, tab)).join('')}</ul>`;
  };

  const tabs = [
    { id: 'dep' as const, label: 'Departures', count: data.departures.length },
    { id: 'arr' as const, label: 'Arrivals', count: arrivals.length },
  ];

  el.innerHTML = `
    ${head(data.name, `${data.stances} stance${data.stances === 1 ? '' : 's'}`)}
    ${showArrivals ? tabStrip(tabs, stationTab) : ''}
    <div data-role="brd-body">${body(stationTab)}</div>
    ${data.byName ? '' : `<p class="ap-note">Stance names were not recognised in this
      timetable, so this covers every stop within a short walk of the station —
      a few of these may be on-street.</p>`}
    <p class="ap-note">Scheduled times from the published timetable, not live
    tracking — a bus running late will still show its booked time.</p>`;

  if (showArrivals) wireTabs<StationTab>(el, body, (t) => { stationTab = t; });
}

// ── Weather strip ───────────────────────────────────────────────────────────

export interface Weather {
  temperatureC: number | null; windKn: number | null; gustKn: number | null;
  windFrom: number | null; beaufort: number | null; beaufortName: string;
  conditions: string; waveHeightM: number | null; seaState: string; updatedAt: number;
  seaTempC: number | null; seaBuoy: string; seaBuoyKm: number;
  /** True when the land half is a Met Office observation, not a forecast. */
  observed?: boolean;
  /** When that observation was taken, epoch ms. */
  observedAt?: number;
  /** Where it was taken, e.g. "Aberdeen". */
  station?: string;
  /** Computed rather than fetched — see backend lib/sun.ts. Epoch ms. */
  sunrise?: number | null;
  sunset?: number | null;
  /** "17h 56m". */
  dayLength?: string;
  /** "4 minutes longer than yesterday". */
  dayChange?: string;
}

export function fetchWeather(): Promise<Weather> {
  return getJson<Weather>('/weather');
}

/** Compass point from degrees — eight points is enough at a glance. */
function compass(deg: number): string {
  return ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(deg / 45) % 8]!;
}

/**
 * Condition glyph. Open-Meteo's WMO codes are collapsed to a handful of words
 * by the backend, so this matches on those rather than re-deriving them.
 *
 * DESIGN NOTE, flagged as the handoff asked: the mapping below is a product
 * call, not a fact. The backend currently emits Clear, Partly cloudy,
 * Overcast, Fog, Drizzle, Rain, Snow, Showers, Snow showers, Thunderstorm —
 * all ten are covered. If those strings change, this needs revisiting.
 */
function conditionGlyph(conditions: string): string {
  const c = conditions.toLowerCase();
  const sun = '<circle cx="15" cy="13" r="7" fill="#ffd60a"/>';
  const cloud = `
    <rect x="6" y="16" width="24" height="9" rx="4.5" fill="#b6c2ce"/>
    <rect x="11" y="11" width="14" height="10" rx="5" fill="#cdd8e3"/>`;
  const drops = `
    <line x1="12" y1="27" x2="10" y2="32" stroke="#4cc2ff" stroke-width="2" stroke-linecap="round"/>
    <line x1="19" y1="27" x2="17" y2="32" stroke="#4cc2ff" stroke-width="2" stroke-linecap="round"/>
    <line x1="26" y1="27" x2="24" y2="32" stroke="#4cc2ff" stroke-width="2" stroke-linecap="round"/>`;

  let inner: string;
  if (/thunder/.test(c)) {
    inner = `${cloud}<polygon points="17,26 23,26 19,33 24,33 15,42 18,32 14,32" fill="#ffd60a"/>`;
  } else if (/snow/.test(c)) {
    inner = `${cloud}<circle cx="12" cy="30" r="2" fill="#e8eef5"/>
      <circle cx="19" cy="31" r="2" fill="#e8eef5"/><circle cx="26" cy="30" r="2" fill="#e8eef5"/>`;
  } else if (/rain|drizzle|shower/.test(c)) {
    inner = `${cloud}${drops}`;
  } else if (/fog/.test(c)) {
    inner = `${cloud}
      <line x1="7" y1="30" x2="29" y2="30" stroke="#8b9bad" stroke-width="2" stroke-linecap="round"/>
      <line x1="10" y1="35" x2="26" y2="35" stroke="#8b9bad" stroke-width="2" stroke-linecap="round"/>`;
  } else if (/overcast/.test(c)) {
    inner = cloud;
  } else if (/partly|cloud/.test(c)) {
    inner = `${sun}${cloud}`;
  } else {
    inner = '<circle cx="18" cy="18" r="11" fill="#ffd60a"/>';
  }
  return `<svg class="wx-glyph" viewBox="0 0 36 44" width="36" height="44" aria-hidden="true">${inner}</svg>`;
}

export function renderWeather(el: HTMLElement, w: Weather): void {
  if (!w.updatedAt) {
    el.innerHTML = '';
    return;
  }
  const wind = w.windKn === null ? '' :
    `${Math.round(w.windKn)} kn${w.windFrom !== null ? ` ${compass(w.windFrom)}` : ''}`;
  const gust = w.gustKn !== null && w.windKn !== null && w.gustKn > w.windKn + 3
    ? ` (gusting ${Math.round(w.gustKn)})` : '';

  // The arrow points the way the wind is going, i.e. the reported bearing it
  // comes FROM plus 180. The -90 offset is because the glyph is drawn pointing
  // east in its own box.
  const dial = w.windFrom === null ? '' : `
    <div class="wx-dial">
      <div class="wx-circle">
        <svg viewBox="0 0 34 34" width="34" height="34" aria-hidden="true"
             style="transform: rotate(${w.windFrom + 180 - 90}deg)">
          <path d="M6 17 H24 M18 11 L25 17 L18 23" fill="none" stroke="#e8eef5"
                stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <span class="wx-dial-label">${escapeHtml(compass(w.windFrom))}</span>
    </div>`;

  const temp = w.temperatureC === null ? '' :
    `<span class="wx-temp">${Math.round(w.temperatureC)}°</span>`;

  // Wind and sea are what govern whether anything sails or flies here, so they
  // lead; temperature is incidental by comparison.
  el.innerHTML = `
    <div class="wx-top">
      ${dial}
      <div class="wx-cond-block">
        ${conditionGlyph(w.conditions)}
        ${temp}
        <span class="wx-cond-name">${escapeHtml(w.conditions)}</span>
      </div>
    </div>
    <div class="wx-row"><span class="wx-k">Wind</span>
      <span class="wx-v">${escapeHtml(wind)}${escapeHtml(gust)}</span></div>
    ${w.beaufortName ? `<div class="wx-sub">F${w.beaufort} ${escapeHtml(w.beaufortName)}</div>` : ''}
    ${w.waveHeightM !== null ? `
      <div class="wx-row"><span class="wx-k">Sea</span>
        <span class="wx-v">${w.waveHeightM.toFixed(1)} m</span></div>
      <div class="wx-sub">${escapeHtml(w.seaState)}${w.seaBuoy
        ? ` · ${escapeHtml(w.seaBuoy)} buoy, ${w.seaBuoyKm} km` : ''}</div>` : ''}
    ${w.seaTempC !== null ? `
      <div class="wx-row"><span class="wx-k">Sea temp</span>
        <span class="wx-v">${w.seaTempC.toFixed(1)}°</span></div>` : ''}
    ${sunRow(w)}
    ${weatherCredit(w)}`;
}

/**
 * Sunrise, sunset, and how the day is moving.
 *
 * Worth its place here more than it would be further south. Aberdeen runs from
 * under seven hours of daylight at midwinter to nearly eighteen at midsummer,
 * and around the equinoxes the day changes by four minutes every twenty-four
 * hours — so "when does it get dark" is a live question, and how fast it is
 * changing is the part people actually remark on.
 *
 * No credit line: this is computed from the date and the city's coordinates,
 * not fetched from anyone. It is the only row in this panel that cannot go
 * stale because somebody else's service is down.
 */
function sunRow(w: Weather): string {
  if (!w.sunrise || !w.sunset) return '';
  const clock = (ms: number) => new Date(ms).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit',
  });
  return `
    <div class="wx-row"><span class="wx-k">Sun</span>
      <span class="wx-v wx-sun">
        <span title="Sunrise">&#9650; ${escapeHtml(clock(w.sunrise))}</span>
        <span title="Sunset">&#9660; ${escapeHtml(clock(w.sunset))}</span>
      </span></div>
    ${w.dayLength ? `<div class="wx-sub">${escapeHtml(w.dayLength)} of daylight${
      w.dayChange ? ` · ${escapeHtml(w.dayChange)}` : ''}</div>` : ''}`;
}

/**
 * Who supplied this, and — for an observation — when it was taken.
 *
 * Not presentation. The Met Office licence requires the acknowledgement
 * wherever the data is shown (Terms Part III §2.6) and forbids removing it
 * (§2.9.6); Open-Meteo's CC BY 4.0 requires the same when the fallback is what
 * is on screen; Cefas's OGL requires it for the sea half. The panel names
 * whichever supplied the land reading — never both, because only one of them
 * did — and adds Cefas whenever a sea row is showing.
 *
 * The observation time is here because it is the honest part of the upgrade:
 * this is a real reading from a real station at a stated moment, not a model's
 * estimate of now, and saying so is the whole reason for the change.
 */
function weatherCredit(w: Weather): string {
  const mo = '<a href="https://www.metoffice.gov.uk/" target="_blank" rel="noopener noreferrer">Met Office</a>';
  const om = '<a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer">Open-Meteo</a>';
  const cc = '<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">CC BY 4.0</a>';
  const cefas = '<a href="https://wavenet.cefas.co.uk/" target="_blank" rel="noopener noreferrer">Cefas WaveNet</a>';

  // Cefas is credited whenever their data is on screen, whichever supplier the
  // land half came from. OGL makes the acknowledgement a condition, and the
  // condition attaches to the data rather than to the rest of the panel.
  const sea = w.waveHeightM !== null || w.seaTempC !== null ? ` · sea from ${cefas}` : '';

  if (!w.observed) return `<p class="wx-credit">${om} · ${cc}${sea}</p>`;

  const at = w.observedAt
    ? new Date(w.observedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : '';
  const where = w.station ? escapeHtml(w.station) : '';
  const observedLine = at
    ? `Observed ${where ? `at ${where} ` : ''}${at}`
    : `Observed${where ? ` at ${where}` : ''}`;

  return `<p class="wx-credit">${observedLine} · Data supplied by the ${mo}${sea}</p>`;
}
